import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Line, Pie } from "react-chartjs-2";
import axios from "axios";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

// Register required chart components
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend);

const CandidateResults = () => {
  const { jobId } = useParams(); // Get jobId from URL
  const [candidateData, setCandidateData] = useState(null);

  useEffect(() => {
    axios.get(`http://localhost:5000/api/candidate-results/${jobId}`)
      .then((response) => {
        if (response.data.length > 0) {
          setCandidateData(response.data[0]); // Assuming first candidate result
        }
      })
      .catch((error) => console.error("Error fetching candidate data:", error));
  }, [jobId]);

  if (!candidateData) {
    return <p className="text-center mt-6 text-gray-700">Loading candidate results...</p>;
  }

  // Extract data for charts
  const labels = candidateData.emotions.map((data) => data.time);
  const confidenceData = candidateData.emotions.map((data) => data.confidence);
  const stressData = candidateData.emotions.map((data) => data.stress);
  const engagementData = candidateData.emotions.map((data) => data.engagement);

  // Line Chart Data (Emotional Trends Over Time)
  const lineData = {
    labels: labels,
    datasets: [
      { label: "Confidence (%)", data: confidenceData, borderColor: "#4CAF50", fill: false },
      { label: "Stress (%)", data: stressData, borderColor: "#FF5733", fill: false },
      { label: "Engagement (%)", data: engagementData, borderColor: "#3F51B5", fill: false }
    ],
  };

  // Pie Chart Data (Emotion Breakdown)
  const pieData = {
    labels: ["Confidence", "Stress", "Engagement"],
    datasets: [
      {
        data: [
          confidenceData.reduce((acc, val) => acc + val, 0) / confidenceData.length,
          stressData.reduce((acc, val) => acc + val, 0) / stressData.length,
          engagementData.reduce((acc, val) => acc + val, 0) / engagementData.length,
        ],
        backgroundColor: ["#4CAF50", "#FF5733", "#3F51B5"],
      },
    ],
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-center">Candidate Assessment Results</h2>

      {/* Candidate Summary */}
      <div className="bg-white shadow-md rounded p-4 mb-6 text-center">
        <h3 className="text-lg font-semibold">Candidate ID: {candidateData.candidateId}</h3>
        <p className="text-gray-700">Overall Score: {candidateData.overallScore}%</p>
      </div>

      {/* Line Graph - Emotional Trends */}
      <div className="bg-white shadow-md rounded p-4 mb-6">
        <h3 className="text-lg font-semibold mb-2 text-center">Emotional Trends Over Time</h3>
        <Line data={lineData} />
      </div>

      {/* Pie Chart - Emotion Breakdown */}
      <div className="bg-white shadow-md rounded p-4 mb-6">
        <h3 className="text-lg font-semibold mb-2 text-center">Emotion Distribution</h3>
        <Pie data={pieData} />
      </div>

      {/* Heatmap Table - Emotional Intensity */}
      <div className="bg-white shadow-md rounded p-4">
        <h3 className="text-lg font-semibold mb-2 text-center">Emotion Intensity Table</h3>
        <table className="min-w-full bg-white border border-gray-300 text-center">
          <thead>
            <tr className="bg-gray-200">
              <th className="py-2 px-4 border">Time</th>
              <th className="py-2 px-4 border">Confidence (%)</th>
              <th className="py-2 px-4 border">Stress (%)</th>
              <th className="py-2 px-4 border">Engagement (%)</th>
            </tr>
          </thead>
          <tbody>
            {candidateData.emotions.map((entry, index) => (
              <tr key={index} className="border">
                <td className="py-2 px-4">{entry.time}</td>
                <td className="py-2 px-4">{entry.confidence}</td>
                <td className="py-2 px-4">{entry.stress}</td>
                <td className="py-2 px-4">{entry.engagement}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI-Generated Insights */}
      <div className="bg-white shadow-md rounded p-4 mt-6 text-center">
        <h3 className="text-lg font-semibold mb-2">AI-Generated Insights</h3>
        <p className="text-gray-700">{candidateData.insights}</p>
      </div>
    </div>
  );
};

export default CandidateResults;

